import { ReactNode } from 'react'
import { useFormikContext } from 'formik'
import { AnySchema } from 'yup'

import withForm from 'lib/formik/with-form'
import useValidatedSave from 'lib/formik/use-validated-save'
import {
  ProbateEstate,
  ProbateEstateAttributes,
} from 'lib/models/probate-estate'

import Section from './section'
import useSectionToggle from '../use-section-toggle'

type HandleSave = (event: { name: string; value: unknown }) => void
type EstateField = keyof ProbateEstateAttributes

type Props = {
  heading: string
  toggleName: EstateField
  notesName: EstateField
  fields: EstateField[]
  schema: AnySchema
  probateEstate: ProbateEstate
  saveProbateEstateField: HandleSave
  children: (handleSave: ReturnType<typeof useValidatedSave>) => ReactNode
}

// a toggled card of probate estate fields; radio answers can't be cleared,
// so only the notes lock the toggle
const EstateFields = ({
  heading,
  toggleName,
  notesName,
  schema,
  probateEstate,
  saveProbateEstateField,
  children,
}: Omit<Props, 'fields'>) => {
  const { values } = useFormikContext<Record<string, unknown>>()

  const handleSave = useValidatedSave({
    schema,
    save: saveProbateEstateField,
  })

  const toggle = useSectionToggle({
    name: toggleName,
    initialValue: probateEstate.attributes[toggleName] as boolean | undefined,
    save: saveProbateEstateField,
  })

  return (
    <Section
      heading={heading}
      notesName={notesName}
      onNotesSave={handleSave}
      toggle={{ ...toggle, disabled: !!values[notesName] }}
    >
      {children(handleSave)}
    </Section>
  )
}

const EstateFieldsForm = withForm(EstateFields)

const EstateFieldsSection = ({ fields, ...props }: Props) => (
  <EstateFieldsForm
    initialValues={Object.fromEntries(
      [...fields, props.notesName].map((field) => [
        field,
        props.probateEstate.attributes[field] ?? null,
      ])
    )}
    validationSchema={props.schema}
    {...props}
  />
)

export default EstateFieldsSection
